import { push } from 'connected-react-router';
import { actions as authActions, utils as authUtils } from '@saucelabs/sl-container-authentication';
import { actions as errorActions } from '@saucelabs/sl-container-error-pages';
import { actions as notifications } from '@saucelabs/sl-container-notifications';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { operations as userOperations } from '@saucelabs/sl-module-user';
import { createApiAction } from '@saucelabs/sl-util-redux';
import { env, DEFAULT_DC } from '@saucelabs/sl-util-config';
import { recordTrack } from '@saucelabs/sl-util-segmentio';
import { cookiesHelper } from '@saucelabs/sl-util-helpers';
import { add } from 'date-fns';

import * as types from './types';


export const initialize = createApiAction(
  types.INITIALIZE,
  (payload, meta, dispatch) => (
    dispatch(authActions.getJWTWhoami(null, { skipErrorPage: true }))
      .then(() => (
        Promise.all([
          dispatch(authActions.getWhoami(null, { skipErrorPage: true })),
          dispatch(errorActions.hideErrorPage()), // hide error page from <LoginRequired />
        ])
      ))
      .catch(() => {}) // we don't want the error to be sent to sentry
  ),
);

export const resetPassword = (payload) => (dispatch) => (
  dispatch(authActions.resetPassword(payload))
    .then(({ data: { message } }) => dispatch(push({
      pathname: '/login',
      state: { success: message },
    })))
);

export const sendPasswordReset = (payload) => (dispatch) => (
  dispatch(authActions.sendPasswordReset(payload))
    .then(({ data: { message } }) => dispatch(push({
      pathname: '/login',
      state: { success: message },
    })))
);

export const getCookieDomain = () => {
  if (env === 'production') {
    return '.saucelabs.com';
  }

  if (env === 'staging') {
    return '.saucelabs.net';
  }

  return window.location.hostname;
};

export const verifyEmail = ({ authenticated, verifier }) => (dispatch) => (
  dispatch(authActions.verifyEmail({ verifier }))
    .then(({ data: { message } }) => {
      dispatch(push({
        state: {
          success: message,
        },
      }));
      dispatch(
        recordTrack(
          'User verified email address',
        ),
      );
    })
    .catch(({ response: { data: { message } } }) => {
      if (authenticated) {
        dispatch(push({
          pathname: '/dashboard/tests/vdc',
        }));
        dispatch(notifications.notifyWarning(message, null, true));
      } else {
        const domain = getCookieDomain();
        const expireDate = add(new Date(), { hours: 2 });

        cookiesHelper.setItem('AM_MESSAGES', message, expireDate, '/', domain, false);
        authUtils.redirectToMainLogin();
      }
    })
);

export const sendVerificationEmail = (payload) => (dispatch) => (
  dispatch(authActions.sendVerificationEmail(payload))
    .then(() => dispatch(notifications.notifySuccess('Verification email resent!')))
    .catch(({ response: { data: { message } } }) => (
      dispatch(notifications.notifyError(message))
    ))
);

export const changeEmail = (payload) => (dispatch) => (
  dispatch(authActions.changeEmail(payload))
    .then(() => (
      Promise.all([
        dispatch(notifications.notifySuccess('Email successfully changed! Verification email sent!')),
        dispatch(modalActions.hideModal(types.CHANGE_EMAIL_MODAL)),
      ])
    ))
);

// Find out what kind of redirect to the application is needed.
/* eslint camelcase: 0 */
export const decideWhereToRedirect = ({ data_center = DEFAULT_DC }) => {
  if (
    env !== 'production' ||
    window.location.hostname.includes('.restricted.') ||
    window.location.hostname.includes('build.saucelabs.com')
  ) {
    return window.location.hostname;
  }

  if (data_center === DEFAULT_DC) {
    return 'app.saucelabs.com';
  }

  return `app.${data_center}.saucelabs.com`;
};

export const login = ({ nextQueryParam, ...values }) => (dispatch) => (
  dispatch(authActions.login(values))
    .then(({ data: { target } }) => {
      // since encore returns always beta in response, we will end up in
      // /beta/beta/something path workaround can be removed after
      // https://saucedev.atlassian.net/browse/WEBC-1914 will be done
      const pathname = (nextQueryParam || target).replace(/\/beta/i, '');
      const { hostname } = window.location;

      // redirect to another domain, used by other apps, like admin panel
      if (pathname.startsWith('http')) {
        window.location.assign(pathname);
        return null;
      }

      const redirectionUrl = decideWhereToRedirect({ data_center: values.data_center });
      const redirectToApplication = hostname === redirectionUrl ?
        () => dispatch(push({ pathname })) :
        () => { window.location.assign(`https://${redirectionUrl}${pathname}`); };

      dispatch(recordTrack('User Logged In', { username: values.username }));
      dispatch(userOperations.updateDataCenter({ dcName: values.data_center }));
      return redirectToApplication();
    })
    .catch((error) => {
      if (navigator.userAgent.indexOf('PD-TESTS') === -1) {
        dispatch(recordTrack('User Failed To Log In', { error }));
      }
      throw error;
    })
);
